import type {
  MsRecommendationProcessedResponse,
  ProcessedRecommendationItem,
} from '@/domains/productDiscovery/Recommendation/interfaces/msRecommendation';
import type { Product } from '@/domains/productDiscovery/Recommendation/interfaces/product';
import type {
  RecommendationPlaylist,
  Recommendations,
} from '@/domains/productDiscovery/Recommendation/interfaces/productRecommendation';

export const reorderProduct = (
  config: ProcessedRecommendationItem,
  products: Product[],
  customerProductPreferences?: Set<number>,
): Product[] => {
  const { productIds, modelIds } = config;

  if (!productIds && !modelIds) {
    return products;
  }

  if (productIds) {
    const productByProductId: Map<string, Product> = products.reduce(
      (accum, product) => {
        accum.set(product.productId, product);

        return accum;
      },
      new Map<string, Product>(),
    );

    return productIds?.reduce((accum, id: number) => {
      const product = productByProductId.get(String(id));
      if (product) accum.push(product);
      return accum;
    }, [] as Product[]);
  }

  const productsByModelId: Map<number, Product> = products.reduce(
    (accum, product) => {
      accum.set(product.modelId, product);

      return accum;
    },
    new Map<number, Product>(),
  );

  const configWithProductPreferences =
    modelIds?.filter((modelId) => customerProductPreferences?.has(modelId)) ||
    [];
  const configWithOutProductPreferences =
    modelIds?.filter((modelId) => !customerProductPreferences?.has(modelId)) ||
    [];

  const productsWithPreferences = configWithProductPreferences?.reduce(
    (accum, id: number) => {
      const product = productsByModelId.get(id);
      if (product) accum.push(product);
      return accum;
    },
    [] as Product[],
  );

  const productsWithOutPreferences = configWithOutProductPreferences?.reduce(
    (accum, id: number) => {
      const product = productsByModelId.get(id);
      if (product) accum.push(product);
      return accum;
    },
    [] as Product[],
  );

  return [...productsWithPreferences, ...productsWithOutPreferences];
};

export const createPlaylistContainer = (
  recoConfig: MsRecommendationProcessedResponse,
  algoliaResults: Recommendations,
  customerProductPreferences?: Set<number>,
) => {
  const playlists = algoliaResults.map((res, index) => {
    const {
      name,
      engineName,
      originalName,
      displayType,
      displayAddToCartButton,
    } = recoConfig[index];

    return {
      ...res,
      engineName,
      originalName,
      products: reorderProduct(
        recoConfig[index],
        res.products,
        customerProductPreferences,
      ),
      title: name,
      displayType,
      displayAddToCartButton,
    } as RecommendationPlaylist;
  });

  return playlists;
};
